import { useMemo } from 'react';
import classNames from 'classnames';
import { Control, Controller } from 'react-hook-form';
import { MdDeleteOutline, MdOutlineLink } from 'react-icons/md';
import { IconButton, Input } from '@ds';

interface FormLinkProps {
  control: Control;
  formFieldName: string;
  isEditing: boolean;
  url: string;
  deleteLink: () => void;
  disabled?: boolean;
}

const PROTOCOLS = {
  http: 'http://',
  https: 'https://',
};

const FormLink = ({ control, formFieldName, isEditing, url, deleteLink, disabled }: FormLinkProps) => {
  const href = useMemo(() => {
    if (url.startsWith(PROTOCOLS.http) || url.startsWith(PROTOCOLS.https)) {
      return url;
    }

    return `${PROTOCOLS.http}${url}`;
  }, [url]);

  return (
    <div
      className={classNames('flex min-w-0 w-full max-w-full items-center gap-2 overflow-hidden', {
        'opacity-50 cursor-not-allowed': disabled,
        'cursor-pointer': !disabled,
      })}
    >
      {isEditing ? (
        <div className="min-w-0 w-full max-w-full">
          <Controller
            name={`${formFieldName}.url`}
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <Input
                className="min-w-0 w-full rounded-lg"
                placeholder="https://www.example.com"
                aria-label="Enter URL"
                type="text"
                isInvalid={invalid}
                errorMessage={error?.message}
                startContent={<MdOutlineLink size={24} className="shrink-0 text-base-30" />}
                endContent={
                  !disabled && (
                    <IconButton
                      aria-label="Delete link"
                      className="shrink-0"
                      size="sm"
                      variant="light"
                      onClick={deleteLink}
                      startContent={<MdDeleteOutline size={24} />}
                    />
                  )
                }
                {...field}
                disabled={disabled}
              />
            )}
          />
        </div>
      ) : (
        <div className="flex min-w-0 w-full max-w-full flex-1 items-center overflow-hidden rounded-lg bg-base-60 px-3">
          <MdOutlineLink size={24} className="shrink-0 pr-[2px] text-base-30" />

          <div className="min-w-0 flex-1 overflow-hidden">
            <a
              onClick={(e) => e.stopPropagation()}
              className="ml-[4px] block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-info-30 hover:underline"
              href={href}
              title={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {url}
            </a>
          </div>

          {!disabled && (
            <IconButton
              aria-label="Delete link"
              className="shrink-0"
              size="sm"
              variant="light"
              onClick={deleteLink}
              startContent={<MdDeleteOutline size={24} />}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default FormLink;
